import { computed, Directive, effect, ElementRef, inject, input, Renderer2 } from '@angular/core'
import type { IconNode } from 'lucide'
import { createElement } from 'lucide'
import type { IconColor } from '@awork/_shared/types/color'
import type { IconSize } from '@awork/_shared/types/size'
import { icons } from './types'

@Directive({
  selector: 'i[awIcon]',
  host: { 'aria-hidden': 'true' }
})
export class IconsDirective {
  private el = inject(ElementRef)
  private renderer = inject(Renderer2)
  private previousSize?: IconSize
  private previousColor?: IconColor

  readonly awIcon = input<string>()
  readonly iconSize = input<IconSize>()
  readonly iconColor = input<IconColor>()

  readonly iconNode = computed(() => this.getIconNode())
  readonly iconName = computed(() => this.getIconName())

  constructor() {
    effect(() => this.onIconChange())
  }

  /**
   * Gets the icon name
   * @returns {string}
   */
  private getIconName(): string {
    const hostElement = this.el?.nativeElement as HTMLElement

    return this.awIcon() || hostElement?.textContent || ''
  }

  /**
   * Resolves the icon name through the shared web and mobile map.
   * @returns {IconNode | undefined}
   */
  private getIconNode(): IconNode | undefined {
    const iconName = this.iconName()?.trim()

    return (icons as Partial<Record<string, IconNode>>)[iconName]
  }

  /**
   * Handles changes to the directive inputs
   * @withSignal {Signal<IconNode>} iconNode
   * @withSignal {Signal<IconSize>} iconSize
   * @withSignal {Signal<IconColor>} iconColor
   */
  private onIconChange(): void {
    const icon = this.iconNode()
    const size = this.iconSize()
    const color = this.iconColor()

    if (!this.el) {
      return
    }

    const iconWrapper: HTMLElement = this.el.nativeElement

    if (!icon) {
      iconWrapper.replaceChildren()

      return
    }

    if (this.previousSize) {
      iconWrapper.classList.remove('aw-2-icn--' + this.previousSize)
    }

    if (this.previousColor) {
      iconWrapper.classList.remove('aw-2-icn--' + this.previousColor)
    }

    this.previousSize = size
    this.previousColor = color

    const svg = createElement(icon, {
      width: '1em',
      height: '1em'
    })

    iconWrapper.textContent = ''

    if (iconWrapper) {
      // Adds all the classes applied on the host element
      iconWrapper.classList.add('aw-2-flx-inline')
      iconWrapper.dataset.icon = this.iconName()

      if (size) {
        iconWrapper.classList.add('aw-2-icn--' + size)
      }

      if (color) {
        iconWrapper.classList.add('aw-2-icn--' + color)
      }

      this.renderer.appendChild(iconWrapper, svg)
    }
  }
}
